import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, TextField, IconButton, Checkbox,
    FormControlLabel, Box, Grid2, Select, MenuItem
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Formik, Form, Field, FieldArray, useFormikContext } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { myApi } from '../../../../config/axiosConfig';
import CircleOverlay from '../../components/CircleOverlay';
import CircleItem from '../../components/CircleItem';
import { Edit as EditIcon } from '@mui/icons-material';
import ModalMatriz from './ModalMatriz';
import { base_url_clean } from '../../../../config/urls';

function ModalEditarPregunta({ open, onClose, selectedPregunta, imagenTema, refresh }) {

    const [isLocked, setIsLocked] = useState(false);
    const [circles, setCircles] = useState([]);
    const [circleCoords, setCircleCoords] = useState(null);



    /*=============================================
    VALORE INICIALES DEL FORMULARIO
    =============================================*/
    const initialValues = {
        id_pregunta: selectedPregunta?.id_pregunta || null,
        texto_pregunta: selectedPregunta?.texto_pregunta || '',
        tipo_pregunta: selectedPregunta?.tipo_pregunta || 'texto',
        es_obligatoria: selectedPregunta?.es_obligatoria === 1,
        requiere_foto: selectedPregunta?.requiere_foto === 1,
        permite_nota: selectedPregunta?.permite_nota === 1,
        opciones_respuesta: selectedPregunta?.opciones_respuesta
            ? selectedPregunta.opciones_respuesta.split('|') // <- aquí el parse
            : [],
        respuestas_correctas: selectedPregunta?.respuestas_correctas
            ? selectedPregunta.respuestas_correctas.split('|')
            : [],
        circulos: selectedPregunta?.circulo || [],
        id_tema: selectedPregunta?.tema || null,
        tipo_tema: selectedPregunta?.tipo_tema || null,
        item: selectedPregunta?.item_matriz || null
    };

    /*=============================================
    DEFINICIÓN DE LA VALIDACIÓN DEL FORMULARIO
    =============================================*/
    const validationSchema = Yup.object({
        texto_pregunta: Yup.string().required('Campo obligatorio'),
        opciones_respuesta: Yup.array()
            .of(
                Yup.string()
                    .trim()
                    .required('La opción no puede estar vacía')
            )
            .min(2, 'Debes ingresar al menos 2 opciones de respuesta'),
        //circulos: Yup.array().min(1, 'Debes agregar al menos un punto de inspección')
        respuestasCorrectas: Yup.lazy((value, { parent }) => {
            const tipo = parent.tipo_pregunta;

            if (tipo === 'seleccion_unica' || tipo === 'seleccion_multiple') {
                return Yup.array()
                    .of(Yup.string())
                    .min(1, 'Debes marcar al menos una respuesta como correcta');
            }

            return Yup.array().notRequired(); // default para otros tipos
        }),
    });

    /*=============================================
    FUNCION QUE ACTUALIZA LA PREGUNTA
    =============================================*/
    const handleSubmitForm = async (values) => {
        try {
            let payload = {};
            const opciones_respuesta = values.opciones_respuesta.join('|');
            const respuestas_correctas = values.respuestas_correctas.join('|');

            if (values.tipo_tema === 'matriz') {
                const { item, ...rest } = values;

                payload = {
                    ...rest,
                    item,
                    opciones_respuesta,
                    respuestas_correctas
                };
            } else {
                const { circulos, ...rest } = values;
                const circulo = circulos.length > 0 ? { ...circulos[0], radio: circulos[0].radio || 10 } : null;

                payload = {
                    ...rest,
                    circulo,
                    opciones_respuesta,
                    respuestas_correctas
                };
            }

            const formData = new FormData();
            formData.append("values", JSON.stringify(payload));

            // Solo si hay una imagen nueva (blob o File)
            if (
                values.tipo_tema === 'matriz' &&
                values.item?.imageFile instanceof File
            ) {
                formData.append("file", values.item.imageFile);
            }

            const response = await myApi.put(`/inspeccion/preguntas/updatePregunta`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });

            if (response.data.success) {
                Swal.fire({ icon: 'success', title: 'Pregunta actualizada' });
                handleClose();
                await refresh();
            } else {
                Swal.fire({ icon: 'error', title: 'Error al actualizar' });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({ icon: 'error', title: 'Error al actualizar' });
        }
    };

    /*=============================================
    FUNCION QUE DIBUJA EL CIRCULO EN EL CANVAS
    =============================================*/
    useEffect(() => {
        if (selectedPregunta?.circulo) {
            const { x, y, radio } = selectedPregunta.circulo;

            setCircles([{ x, y, radio, label: "1" }]);
            setIsLocked(true); // para no permitir agregar más
        } else {
            setCircles([]);
            setIsLocked(false);
        }
    }, [selectedPregunta]);

    /*=============================================
    FUNCION QUE PARSEA LAS RESPUESTAS TEXTO A ARRAY
    =============================================*/
    // const parseOpcionesRespuesta = (opcionesRespuesta) => {
    //     let opciones = opcionesRespuesta.split('|');

    //     return opciones;
    // };

    const handleClose = () => {
        setCircles([]);
        onClose();
    };

    const opcionesTipoPregunta = [
        { value: 'texto', label: 'Texto' },
        { value: 'numerica', label: 'Número' },
        { value: 'seleccion_unica', label: 'Opción única' },
        { value: 'seleccion_multiple', label: 'Opción múltiple' },
        { value: 'fecha', label: 'Fecha' },
    ];

    //#region FUNCIONES DE ITEMS DE MATRIZ;
    const [openModalMatriz, setOpenModalMatriz] = useState(false);
    const [itemSelected, setItemSelected] = useState(null);
    const handleEditItem = (item) => {
        setItemSelected(item);
        setOpenModalMatriz(true);
    };

    //#endregion


    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Editar Pregunta
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmitForm}
                >
                    {({ values, setFieldValue, touched, errors }) => {
                        const handleCircleCreated = (coords) => {
                            if (isLocked) return;

                            const label = (circles.length + 1).toString();
                            const newCircle = { ...coords, label };

                            const newCircles = [...circles, newCircle];
                            setCircleCoords(coords);
                            setCircles(newCircles);
                            setIsLocked(true);

                            setFieldValue('circulos', newCircles);
                        };
                        const handleSetItemMatriz = (input) => {
                            let newItem = null;

                            if (Array.isArray(input) && input.length > 0) {
                                newItem = input[0];
                            } else if (input && typeof input === 'object') {
                                newItem = input;
                            }

                            if (newItem) {
                                const image_url = newItem.imageFile
                                    ? URL.createObjectURL(newItem.imageFile)
                                    : newItem.image || newItem.image_url;

                                const updatedItem = {
                                    ...values.item,         // valores anteriores
                                    ...newItem,             // valores nuevos
                                    image_url               // forzar la nueva ruta de imagen (blob o url existente)
                                };

                                setFieldValue('item', updatedItem);
                            }
                        };
                        return (
                            <Form autoComplete="off">
                                <Grid2 container spacing={2} direction="column">
                                    <Grid2>
                                        <Field
                                            as={TextField}
                                            name="texto_pregunta"
                                            label="Pregunta"
                                            fullWidth
                                            error={touched.texto_pregunta && Boolean(errors.texto_pregunta)}
                                            helperText={touched.texto_pregunta && errors.texto_pregunta}
                                        />
                                    </Grid2>

                                    <Grid2>
                                        <Typography variant="subtitle1" sx={{ mt: 2 }}>
                                            Tipo de pregunta
                                        </Typography>
                                        <Field
                                            as={Select}
                                            name="tipo_pregunta"
                                            fullWidth
                                            error={touched.tipo_pregunta && !!errors.tipo_pregunta}
                                            helperText={touched.tipo_pregunta && errors.tipo_pregunta}
                                            sx={{ mb: 2 }}
                                        >
                                            {opcionesTipoPregunta.map((op) => (
                                                <MenuItem key={op.value} value={op.value}>
                                                    {op.label}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid2>

                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12} sm={4}>
                                            <FormControlLabel
                                                control={
                                                    <Field
                                                        as={Checkbox}
                                                        name="es_obligatoria"
                                                        checked={values.es_obligatoria}
                                                    />
                                                }
                                                label="¿Es obligatoria?"
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={4}>
                                            <FormControlLabel
                                                control={
                                                    <Field
                                                        as={Checkbox}
                                                        name="requiere_foto"
                                                        checked={values.requiere_foto}
                                                    />
                                                }
                                                label="¿Requiere foto?"
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} sm={4}>
                                            <FormControlLabel
                                                control={
                                                    <Field
                                                        as={Checkbox}
                                                        name="permite_nota"
                                                        checked={values.permite_nota}
                                                    />
                                                }
                                                label="¿Permite nota?"
                                            />
                                        </Grid2>
                                    </Grid2>

                                    <Grid2>
                                        <Typography variant="subtitle1">Opciones de respuesta</Typography>
                                        <FieldArray name="opciones_respuesta">
                                            {({ push, remove }) => (
                                                <Box>
                                                    {values.opciones_respuesta.map((opcion, index) => (
                                                        <Box key={index} display="flex" gap={1} mb={1} alignItems="center">
                                                            <Field
                                                                as={TextField}
                                                                name={`opciones_respuesta[${index}]`}
                                                                fullWidth
                                                                value={opcion}
                                                                placeholder={`Opción ${index + 1}`}
                                                                error={
                                                                    touched.opciones_respuesta &&
                                                                    touched.opciones_respuesta[index] &&
                                                                    Boolean(errors.opciones_respuesta?.[index])
                                                                }
                                                                helperText={
                                                                    touched.opciones_respuesta &&
                                                                    touched.opciones_respuesta[index] &&
                                                                    errors.opciones_respuesta?.[index]
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={values.respuestas_correctas.includes(index.toString())}
                                                                        onChange={(e) => {
                                                                            const current = [...values.respuestas_correctas];
                                                                            if (e.target.checked) {
                                                                                current.push(index.toString());
                                                                            } else {
                                                                                const idx = current.indexOf(index.toString());
                                                                                if (idx > -1) current.splice(idx, 1);
                                                                            }
                                                                            setFieldValue('respuestas_correctas', current);
                                                                        }}
                                                                    />
                                                                }
                                                                label="Correcta"
                                                            />
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={() => remove(index)}
                                                                disabled={values.opciones_respuesta.length < 2}
                                                            >
                                                                Eliminar
                                                            </Button>
                                                        </Box>
                                                    ))}
                                                    <Button variant="outlined" onClick={() => push('')}>
                                                        Agregar opción
                                                    </Button>
                                                </Box>
                                            )}
                                        </FieldArray>
                                        {typeof errors.opciones_respuesta === 'string' && touched.opciones_respuesta && (
                                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                                {errors.opciones_respuesta}
                                            </Typography>
                                        )}
                                    </Grid2>
                                    {values.tipo_tema != 'matriz' && (
                                        <>
                                            <Grid2 size={12} container>
                                                <Grid2 size={12}>
                                                    <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        Punto de inspección
                                                    </Typography>
                                                </Grid2>
                                                <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CircleOverlay onCircleCreated={handleCircleCreated} disabled={isLocked} imagen={imagenTema} circles={circles} onCirclesChange={setCircles} />
                                                    {touched.circulos && errors.circulos && (
                                                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                                            {errors.circulos}
                                                        </Typography>
                                                    )}
                                                </Grid2>
                                            </Grid2>
                                            <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

                                                {values.circulos && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        sx={{ mt: 1 }}
                                                        onClick={() => {
                                                            setCircles([]);
                                                            setIsLocked(false);
                                                            setFieldValue('circulos', []);
                                                        }}
                                                    >
                                                        Eliminar punto
                                                    </Button>
                                                )}
                                            </Grid2>
                                        </>

                                    )}

                                    {values.tipo_tema === 'matriz' && values.item && (
                                        <Grid2 container spacing={2}>
                                            <Grid2 size={12}>
                                                <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    Punto de inspección
                                                </Typography>
                                            </Grid2>

                                            <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                    <CircleItem
                                                        key={values.item.id}
                                                        image={
                                                            typeof values.item.image_url === 'string' && values.item.image_url.startsWith('blob:')
                                                                ? values.item.image_url
                                                                : `${base_url_clean}img/items/${values.item.image_url}`
                                                        }
                                                        label={values.item.label}
                                                        size={100}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -8,
                                                            left: -8,
                                                            backgroundColor: 'rgba(255,255,255,0.8)',
                                                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                                                        }}
                                                        onClick={() => handleEditItem(values.item)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Grid2>

                                            {touched.item && errors.item && (
                                                <Grid2 xs={12}>
                                                    <Typography variant="body2" color="error" sx={{ mt: 1, textAlign: 'center' }}>
                                                        {errors.item}
                                                    </Typography>
                                                </Grid2>
                                            )}
                                        </Grid2>
                                    )}


                                    <DialogActions sx={{ mt: 3 }}>
                                        <Button onClick={handleClose} variant="outlined">
                                            Cancelar
                                        </Button>
                                        <Button type="submit" variant="contained" color="primary">
                                            Guardar cambios
                                        </Button>
                                    </DialogActions>
                                    {/* AHORA el ModalMatriz debe ir AQUÍ, fuera del <Form> */}
                                    <ModalMatriz
                                        open={openModalMatriz}
                                        onClose={() => {
                                            setOpenModalMatriz(false);
                                            setItemSelected(null);
                                        }}
                                        setItems={handleSetItemMatriz}
                                        editing={itemSelected}
                                        multiple={false}
                                    />
                                </Grid2>
                            </Form>
                        );
                    }}

                </Formik>
            </DialogContent>
        </Dialog>
    );
}

export default ModalEditarPregunta;